import React, { useState, useContext } from "react";
import { LButton, LFormInput, LDialog } from "@loffa/gpp-modules";
import { AuthContext } from "../../../../../context/AuthContext";
import NotificationContext from "../../../../../context/NotificationContext";

const GATEWAY_BASE = process.env.REACT_APP_API_GATEWAY_BASE;
const USER_ROLES = [
  { value: 2, text: "User" },
  { value: 1, text: "Super Admin" },
];

function UserForm(props) {
  const authContext = useContext(AuthContext);
  const notificationContext = useContext(NotificationContext);
  const [state, setState] = useState({
    user: props.selectedUser ?? {
      userGuid: null,
      firstName: "",
      lastName: "",
      email: "",
      roleTypeId: 2, // User
      isActive: true,
      oktaId: null
    },
    saveSuccessful: false,
    isAdmin: props.isAdmin,
  });

  let handleStateChange = (e) => {
    setState({
      ...state,
      user: {
        ...state.user,
        [e.target.name]: e.target.value,
      },
    });
  };

  let setDialogTitle = () => {
    return props.selectedUser ? "Edit User" : "Add New User";
  };

  const getClientProductUsers = async (userGuid) => {
    let clientProductUsersUrl =
      GATEWAY_BASE + "/multitenancy/api/users/" + userGuid + "/GetClientProductUsers";

    try {
      const res = await fetch(clientProductUsersUrl, {
        method: "GET",
        mode: "cors",
        headers: {
          Authorization: `Bearer ${authContext.authState().accessToken.accessToken}`,
          "Content-Type": "application/json",
        },
      });
      if (!res.ok) throw new Error(`GetClientProductUsers ${res.status}`);
      const response = await res.json().catch(() => null);
      return response;
    } catch (error) {
      console.log(error);
    }
  }

  const getBaseProductUrl = async (productGuid) => {

    let productUrl =
      GATEWAY_BASE + "/multitenancy/api/products/" + productGuid;

    try {
      const res = await fetch(productUrl, {
        method: "GET",
        mode: "cors",
        headers: {
          Authorization: `Bearer ${authContext.authState().accessToken.accessToken}`,
          "Content-Type": "application/json",
        },
      });
      const response = await res.json();
      return response.url;
    } catch (error) {
      console.log(error);
    }

  }

  const getExistingUserPermissions = async (productUrl, clientGuid) => {
    try {
      const res = await fetch(productUrl, {
        method: "GET",
        mode: "cors",
        headers: {
          Authorization: `Bearer ${authContext.authState().accessToken.accessToken}`,
          "Content-Type": "application/json",
          ClientGuid: clientGuid
        },
      });
      const response = await res.json();
      return response;
    } catch (error) {
      console.log(error);
    }
  }

  const getClientProduct = async (clientProductGuid) => {
    let productUrl =
      GATEWAY_BASE + "/multitenancy/api/clients/" + clientProductGuid + "/ClientProduct";

    try {
      const res = await fetch(productUrl, {
        method: "GET",
        mode: "cors",
        headers: {
          Authorization: `Bearer ${authContext.authState().accessToken.accessToken}`,
          "Content-Type": "application/json",
        },
      });
      const response = await res.json();
      return response;
    } catch (error) {
      console.log(error);
    }
  }

  const updateClientDatabase = async (userGuid = null, oktaId = null) => {
    const effectiveUserGuid = userGuid ?? state.user.userGuid;
    const effectiveOktaId = oktaId ?? state.user.oktaId;
    try {
      const clientProductUsers = await getClientProductUsers(effectiveUserGuid);
      if (!Array.isArray(clientProductUsers)) {
        throw new Error(`clientProductUsers (e) not iterable: ${JSON.stringify(clientProductUsers)}`);
      }
      for (const clientProdUser of clientProductUsers) {
        const clientProduct = await getClientProduct(clientProdUser.clientProductGuid);

        const baseUrl = await getBaseProductUrl(clientProduct.productGuid);
        const permissions = await getExistingUserPermissions(baseUrl + "/api/multitenancy/GetExistingUserPermissions?oktaId=" + effectiveOktaId, clientProduct.clientGuid);

        const addOrUpdateEndpoint = "/updateUser";
        const method = "PATCH";
        const url = `${baseUrl}/api/multitenancy${addOrUpdateEndpoint}`;
        const userState = {
          ...state.user,
          userGuid: effectiveUserGuid,
          oktaId: effectiveOktaId,
          isActive: state.user.isActive ?? true,
          isActiveInProduct: state.user.isActive ?? true,
          permissions: {
            accountTypeIds: (permissions?.accountTypes ?? []).map((x) => x.id),
            dtcIds: (permissions?.dtcs ?? []).map((x) => x.id),
            moduleIds: (permissions?.modules ?? []).map((x) => x.id),
            productRoleTypeId: permissions?.roles?.id,
          },
        };

        const response = await fetch(url, {
          method: method,
          mode: "cors",
          headers: {
            Authorization: `Bearer ${authContext.authState().accessToken.accessToken}`,
            "Content-Type": "application/json",
            ClientGuid: clientProduct.clientGuid,
          },
          body: JSON.stringify(userState),
        });

        if (!response.ok) {
          const err = await response.json().catch(() => ({}));
          throw new Error(err?.message || `HTTP ${response.status} updating ${clientProduct.clientGuid}`);
        }
      }

    } catch (error) {
      console.error("Error in addUserToClientProduct:", error);
      throw error;
    }
  }

  let handleSubmit = async (e) => {
    e.preventDefault();
    e.currentTarget.checkValidity();
    let method = state.user.userGuid ? "PATCH" : "POST";
    let url = GATEWAY_BASE + "/multitenancy/api/users";
    const payload = {
      ...state.user,
      isActive: state.user.isActive ?? true,
      lastUpdatedBy: authContext.userInfo().loffaUserGuid,
    };

    // NOTE: If we get a 400 error on this fetch, regarding an invalid Token, it means the Okta generated token is expired,
    // because it hasn't been used in 30 days. We need to regenerate and update the token in app.settings in the MT API Project 

    fetch(url, {
      method: method,
      mode: "cors",
      headers: {
        Authorization: `Bearer ${authContext.authState().accessToken.accessToken}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    }).then(async (res) => {
      let body = {};
      try { body = await res.json(); } catch { }
      if (!res.ok) throw new Error(body?.errorMessage || body?.message || `HTTP ${res.status}`);

      const userGuid = body?.userGuid ?? state.user.userGuid ?? null;
      const oktaId = body?.oktaId ?? state.user.oktaId ?? null;

      setState((prev) => ({
        ...prev,
        user: { ...prev.user, userGuid, oktaId },
      }));
      if (userGuid) {
        return updateClientDatabase(userGuid, oktaId).then(() => ({ userGuid, oktaId }));
      }
      return { userGuid, oktaId };
    })
      .then(({ userGuid }) => {
        props.saveForm?.(userGuid);
        notificationContext.addSuccess(`${payload.firstName} ${payload.lastName} - User Saved.`);
      })
      .catch((err) => {
        props.failedForm?.();
        notificationContext.addError(err?.message || "An Unknown Error has occurred");
      });
  };

  let selectRole = (e) => {
    setState({
      ...state,
      user: {
        ...state.user,
        roleTypeId: e.value.value,
      },
    });
  };

  let toggleActivate = () => {
    setState({
      ...state,
      user: {
        ...state.user,
        isActive: !state.user.isActive,
      },
    });
  }

  return (
    <div>
      <form onSubmit={handleSubmit}>
        <LDialog
          width="500"
          title={setDialogTitle()}
          body={
            <div>
              {state.user.userGuid &&
                state.user.userGuid !==
                authContext.userInfo().loffaUserGuid &&
                (
                  <LButton onClick={toggleActivate} type="submit">
                    {state.user.isActive ? "Deactivate" : "Activate"}
                  </LButton>
                )}
              <div className="required-field">* required</div>
              <LFormInput
                labelName="First Name *"
                type="input"
                onChange={(event) => handleStateChange(event)}
                input={{
                  value: state.user.firstName,
                  onChange: (event) => handleStateChange(event),
                  name: "firstName",
                  required: true,
                }}
              />
              <LFormInput
                labelName="Last Name"
                type="input"
                onChange={(event) => handleStateChange(event)}
                input={{
                  value: state.user.lastName,
                  onChange: (event) => handleStateChange(event),
                  name: "lastName",
                  required: true,
                }}
              />
              <LFormInput
                labelName="Email *"
                type="input"
                onChange={(event) => handleStateChange(event)}
                input={{
                  value: state.user.email,
                  onChange: (event) => handleStateChange(event),
                  name: "email",
                  required: true,
                }}
              />
              {state.isAdmin && (
                <LFormInput
                  labelName="Role *"
                  type="combobox"
                  comboBox={{
                    data: USER_ROLES,
                    textField: "text",
                    dataItemKey: "value",
                    required: true,
                    defaultValue: USER_ROLES.find((role) => {
                      return role.value === state.user.roleTypeId;
                    }),
                    onChange: (event) => selectRole(event),
                  }}
                />
              )}
            </div>
          }
          actionLayout="end"
          action={
            <React.Fragment>
              <LButton onClick={props.closeForm}>Cancel</LButton>
              <LButton themeColor="primary" className="green" type="submit">
                Save
              </LButton>
            </React.Fragment>
          }
          actionCloseButton={props.closeForm}
          actionOverlay={props.closeForm}
        />
      </form>
    </div>
  );
}

export { UserForm };